import { apiAxios } from '@/api/apiConfig';
import type { AxiosRequestConfig } from 'axios';
import type { PlanCode, PaymentStatus, PaymentMethod, ReceiptType, ReceiptIssueStatus } from '@/api/planApi';

// Workspace 통합 모델 3단계 API (백엔드 로드맵 §9) - 레거시 teamApi/planApi와 병행.
// 백엔드 workspace.api.enabled가 꺼져 있으면 이 경로들은 전부 404를 반환한다(컨트롤러
// 빈 자체가 생성 안 됨) - 로컬 개발 시 WORKSPACE_API_ENABLED=true 환경변수로 켜서 사용.

export type WorkspacePlan = 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
export type WorkspaceStatus = 'ACTIVE' | 'INACTIVE' | 'PAST_DUE';
export type WorkspaceMemberRole = 'OWNER' | 'MEMBER';

/** GET /api/workspaces/my/teams 응답 - 레거시 TeamSummary와 별개 타입(축소 DTO) */
export interface WorkspaceSummary {
  workspaceId: number;
  name: string;
  personal: boolean;
  myRole: WorkspaceMemberRole;
  plan: WorkspacePlan;
  monthlyLimit: number;
  status: WorkspaceStatus;
  memberCount: number;
  /** 팀 워크스페이스면 레거시 teams.id, 개인 워크스페이스면 null - 레거시 쓰기 액션(teamApi.*)과 이어 붙일 때 사용 */
  legacyTeamId: number | null;
}

/** GET /api/workspaces/teams/{workspaceId}/usage 응답 - 레거시 TeamUsage와 별개 타입(memberStats/엔터프라이즈 계약 제외) */
export interface WorkspaceTeamUsage {
  workspaceId: number;
  teamName: string;
  plan: WorkspacePlan;
  monthlyLimit: number;
  totalUsed: number;
  remaining: number;
  billingStartDate: string | null;
  billingEndDate: string | null;
}

/**
 * GET /api/workspaces/my/billing 응답 - 레거시 PaymentRecord와 별개 타입(축소 DTO).
 * teamId/contractId/monthlyLimit/monthlyPrice/contractName/teamName/teamRole은
 * 이 DTO에 의도적으로 없음(BE가 아직 "후속 조각"으로 미구현) - FE에서도 아예
 * 타입에 선언하지 않아 실수로 읽을 수 없게 한다. cashReceiptIssued도 legacy와
 * null 처리 방식이 달라(legacy는 항상 boolean으로 정규화, 이쪽은 raw nullable)
 * 의도적으로 타입에서 제외했다 - src/lib/workspacePaymentOverlay.ts 참고.
 */
export interface WorkspacePaymentRecordResponse {
  id: number;
  plan: PlanCode;
  amount: number;
  paymentMethod: PaymentMethod;
  status: PaymentStatus;
  billingMonth: string;
  createdAt: string;
  confirmedAt: string | null;
  canCancel: boolean;
  refundAmount: number | null;
  refundedAt: string | null;
  refundCompletedAt: string | null;
  receiptType: ReceiptType | null;
  receiptIssueStatus: ReceiptIssueStatus | null;
  receiptIssuedAt: string | null;
  receiptIssueFailReason: string | null;
  isDowngrade: boolean | null;
  /** Workspace 네이티브 결제("RECURRING"/"OVERAGE")만 채워짐, 레거시 결제는 항상 null
   * (2026-09-10 신설) - workspacePaymentOverlay.ts의 검증 대상 필드가 아니라 overlay
   * 병합에는 관여하지 않는다. */
  paymentType: string | null;
}

export interface WorkspacePaymentPage {
  content: WorkspacePaymentRecordResponse[];
  totalElements: number;
  totalPages: number;
  number: number;
}

/**
 * GET /api/workspaces/my/billing/native 응답(2026-09-10 신설) - 8/3 컷오버 이후
 * 정기결제/초과과금(workspace_payment_records)이 레거시 결제내역 화면에 전혀 안
 * 보이던 갭을 닫는다. WorkspacePaymentRecordResponse와 동일 shape이지만 취소/환불/
 * 증빙 관련 필드는 백엔드가 항상 false/null로 고정한다(이 결제 유형엔 아직 그
 * 액션들이 구현되지 않았음).
 * <p><b>레거시 결제내역과 절대 병합하지 않는다</b> - id 시퀀스가 레거시
 * {@code PaymentRecord.id}와 완전히 독립적이라, 같은 배열/테이블에 섞으면 환불/영수증
 * 버튼이 엉뚱한 레코드를 대상으로 호출될 위험이 있다. BillingPage.tsx에서 별도
 * 섹션으로만 렌더링한다.</p>
 */
export type WorkspaceNativePaymentRecordResponse = WorkspacePaymentRecordResponse;

/**
 * GET /api/workspaces/my/plan 응답 - 레거시 PlanInfo와 별개 타입(축소 DTO).
 * plan/pendingPlan/status/enterpriseContract는 BE가 응답에 포함하지만 이 DTO에
 * 의도적으로 선언하지 않는다 - plan은 업그레이드/다운그레이드 분기 결정에,
 * enterpriseContract는 PlanPage.tsx의 변경 차단 게이트(활성 계약 여부)에 각각
 * 쓰이는 위험 필드라 overlay 대상에서 제외했다(src/lib/workspacePlanOverlay.ts 참고).
 */
export interface WorkspacePlanOverlayResponse {
  monthlyLimit: number;
  billingCycleDay: number | null;
  canDowngrade: boolean;
  downgradeReason: string | null;
  changePlanReason: string | null;
}

export const workspaceApi = {
  getMyTeams: (config?: AxiosRequestConfig) =>
    apiAxios.get<WorkspaceSummary[]>('/workspaces/my/teams', config),

  getTeamUsage: (workspaceId: number) =>
    apiAxios.get<WorkspaceTeamUsage>(`/workspaces/teams/${workspaceId}/usage`),

  getMyBilling: (params: { page: number; size: number }, config?: AxiosRequestConfig) =>
    apiAxios.get<WorkspacePaymentPage>('/workspaces/my/billing', { ...config, params }),

  getMyNativeBilling: (config?: AxiosRequestConfig) =>
    apiAxios.get<WorkspaceNativePaymentRecordResponse[]>('/workspaces/my/billing/native', config),

  getMyPlan: (config?: AxiosRequestConfig) =>
    apiAxios.get<WorkspacePlanOverlayResponse>('/workspaces/my/plan', config),
};
